import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { MagnifyingGlass, ShieldWarning, Prohibit, X, User, Car, House, Backpack, UsersThree, ChartLineUp, ArrowsClockwise, PlugsConnected, Warning, DiscordLogo } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

const fmt = (n) => new Intl.NumberFormat("de-DE").format(n || 0);

export default function Admin() {
  const [players, setPlayers] = useState([]);
  const [q, setQ] = useState("");
  const [stats, setStats] = useState(null);
  const [detail, setDetail] = useState(null);
  const [action, setAction] = useState(null); // {type: 'warn'|'ban', user}
  const [reason, setReason] = useState("");
  const [permanent, setPermanent] = useState(false);
  const [days, setDays] = useState(7);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [discordStatus, setDiscordStatus] = useState(null);
  const [discordSyncing, setDiscordSyncing] = useState(false);

  const load = async () => {
    try {
      const [p, s, sy, ds] = await Promise.all([
        api.get("/admin/players", { params: q ? { q } : {} }),
        api.get("/admin/stats"),
        api.get("/admin/sync/status").catch(() => ({ data: null })),
        api.get("/admin/discord/status").catch(() => ({ data: null })),
      ]);
      setPlayers(p.data); setStats(s.data); setSyncStatus(sy.data); setDiscordStatus(ds.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Zugriff verweigert");
    }
  };

  const runDiscordSync = async () => {
    setDiscordSyncing(true);
    try {
      const r = await api.post("/admin/discord/sync-roles");
      toast.success(`Rollen synchronisiert — ${r.data.checked} geprüft, ${r.data.updated} aktualisiert`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Rollen-Sync");
    } finally { setDiscordSyncing(false); }
  };

  const runSync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/admin/sync/run");
      const c = r.data.counts;
      toast.success(`Sync fertig — ${c.users} Spieler, ${c.vehicles} Fahrzeuge, ${c.properties} Immobilien, ${c.inventory} Items`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync fehlgeschlagen");
    } finally { setSyncing(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
     
  }, [q]);

  const openDetail = async (userId) => {
    try {
      const r = await api.get(`/admin/players/${userId}`);
      setDetail(r.data);
    } catch { toast.error("Fehler"); }
  };

  const submitAction = async () => {
    if (!reason.trim()) { toast.error("Grund angeben"); return; }
    try {
      if (action.type === "warn") {
        await api.post("/admin/warn", { user_id: action.user.id, reason });
        toast.success("Warnung ausgestellt");
      } else {
        await api.post("/admin/ban", { user_id: action.user.id, reason, permanent, days: Number(days) });
        toast.success("Ban ausgestellt");
      }
      setAction(null); setReason(""); setPermanent(false); setDays(7);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };

  const unban = async (userId) => {
    try {
      await api.post(`/admin/unban/${userId}`);
      toast.success("Entbannt");
      load();
    } catch { toast.error("Fehler"); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="ADMIN // SERVER MANAGEMENT"
        title="Admin Panel"
        description="Spielerverwaltung, Warnungen und Bans."
      />

      <div className="p-4 md:p-8 lg:p-12 space-y-6 md:space-y-8">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatBox testId="adm-total" icon={UsersThree} label="Spieler" value={stats.total_players} tone="volt" />
            <StatBox testId="adm-vehicles" icon={Car} label="Fahrzeuge" value={stats.total_vehicles} />
            <StatBox testId="adm-properties" icon={House} label="Immobilien" value={stats.total_properties} />
            <StatBox testId="adm-warnings" icon={ChartLineUp} label="Warnungen" value={stats.total_warnings} tone="warn" />
            <StatBox testId="adm-bans" icon={Prohibit} label="Aktive Bans" value={stats.active_bans} tone="danger" />
          </div>
        )}

        {discordStatus && (
          <div className="border border-tactical-border bg-tactical-surface" data-testid="discord-panel">
            <div className="p-4 border-b border-tactical-border flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <DiscordLogo size={22} weight="fill" className={discordStatus.ok ? "text-[#5865F2]" : "text-white/40"} />
                <div>
                  <div className="font-mono text-xs text-white/40 uppercase tracking-widest">Discord Whitelist & Rollen</div>
                  <div className="font-display font-bold text-lg">
                    {discordStatus.ok
                      ? <span className="text-tactical-success">● {discordStatus.bot_username} @ {discordStatus.guild_name}</span>
                      : <span className="text-tactical-danger">● Bot Offline: {discordStatus.reason || "unbekannt"}</span>}
                  </div>
                </div>
              </div>
              <button
                data-testid="discord-sync-btn"
                onClick={runDiscordSync}
                disabled={!discordStatus.ok || discordSyncing}
                className="flex items-center gap-2 border border-[#5865F2]/50 bg-[#5865F2]/10 hover:bg-[#5865F2]/20 disabled:opacity-40 disabled:cursor-not-allowed text-[#8b95ff] px-4 py-2 text-xs font-mono uppercase tracking-widest transition-colors"
              >
                <ArrowsClockwise size={14} className={discordSyncing ? "animate-spin" : ""} />
                {discordSyncing ? "Läuft..." : "Rollen syncen"}
              </button>
            </div>
            <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4 font-mono text-xs">
              <SyncCell label="Bot" value={discordStatus.bot_username || "—"} tone="text-white" />
              <SyncCell label="Guild" value={discordStatus.guild_name || "—"} tone="text-white" />
              <SyncCell label="Whitelist Rolle" value={discordStatus.player_role_id ? "✓ gesetzt" : "— offen"} tone={discordStatus.player_role_id ? "text-tactical-success" : "text-white/60"} />
              <SyncCell label="Admin Rolle" value={discordStatus.admin_role_id ? "✓ gesetzt" : "— offen"} tone={discordStatus.admin_role_id ? "text-tactical-success" : "text-white/60"} />
            </div>
            <div className="px-4 pb-4 font-mono text-[10px] text-white/40 space-y-1">
              <div><span className="text-white/60">Player Rolle:</span> <code className="text-white/70">{discordStatus.player_role_id || "—"}</code> · nur User mit dieser Rolle in Discord dürfen sich einloggen</div>
              <div><span className="text-white/60">Admin Rolle:</span> <code className="text-white/70">{discordStatus.admin_role_id || "— nicht gesetzt"}</code> · User mit dieser Rolle werden automatisch App-Admin</div>
              <div className="pt-1"><span className="text-white/60">Verlinkte User:</span> <span className="text-volt">{discordStatus.linked_users ?? 0}</span> · <span className="text-white/60">App-Admins:</span> <span className={discordStatus.admins_by_role > 0 ? "text-tactical-success" : "text-white/60"}>{discordStatus.admins_by_role ?? 0}</span></div>
            </div>
          </div>
        )}

        {syncStatus && (
          <div className="border border-tactical-border bg-tactical-surface" data-testid="sync-panel">
            <div className="p-4 border-b border-tactical-border flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <PlugsConnected size={20} className={syncStatus.enabled ? "text-tactical-success" : "text-white/40"} weight="duotone" />
                <div>
                  <div className="font-mono text-xs text-white/40 uppercase tracking-widest">FiveM Live Sync (ESX)</div>
                  <div className="font-display font-bold text-lg">
                    {syncStatus.enabled
                      ? <span className="text-tactical-success">● Aktiviert</span>
                      : <span className="text-white/60">● Deaktiviert</span>}
                  </div>
                </div>
              </div>
              <button
                data-testid="run-sync-btn"
                onClick={runSync}
                disabled={!syncStatus.enabled || syncing}
                className="flex items-center gap-2 border border-volt/40 bg-volt/10 hover:bg-volt/20 disabled:opacity-40 disabled:cursor-not-allowed text-volt px-4 py-2 text-xs font-mono uppercase tracking-widest transition-colors"
              >
                <ArrowsClockwise size={14} className={syncing ? "animate-spin" : ""} />
                {syncing ? "Sync läuft..." : "Jetzt Syncen"}
              </button>
            </div>
            <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4 font-mono text-xs">
              <SyncCell label="SSH Host" value={syncStatus.ssh_host || "—"} />
              <SyncCell label="SSH User" value={syncStatus.ssh_user || "—"} />
              <SyncCell label="Auth" value={syncStatus.has_password ? "Passwort ✓" : syncStatus.has_key ? "SSH Key ✓" : "Fehlt"} tone={syncStatus.has_password || syncStatus.has_key ? "text-tactical-success" : "text-tactical-danger"} />
              <SyncCell label="Gesynct" value={`${syncStatus.synced_players} Spieler`} tone={syncStatus.synced_players > 0 ? "text-tactical-success" : "text-white/60"} />
            </div>
            {!syncStatus.enabled && (
              <div className="px-4 pb-4 flex items-start gap-2 font-mono text-xs text-white/50">
                <Warning size={14} className="text-tactical-warn mt-0.5 flex-shrink-0" />
                <span>Setze <code className="text-volt">FIVEM_SYNC_ENABLED=true</code> und <code className="text-volt">FIVEM_SSH_PASSWORD</code> (oder <code className="text-volt">FIVEM_SSH_KEY_PATH</code>) in <code className="text-volt">backend/.env</code>, um Live-Sync zu aktivieren.</span>
              </div>
            )}
          </div>
        )}

        <div className="border border-tactical-border bg-tactical-surface">
          <div className="p-4 border-b border-tactical-border flex items-center gap-3">
            <MagnifyingGlass size={18} className="text-white/40" />
            <input
              data-testid="admin-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Spielersuche (Name oder Email)..."
              className="flex-1 bg-transparent outline-none font-mono text-sm text-white placeholder:text-white/30"
            />
            <div className="font-mono text-xs text-white/40">{players.length} Ergebnisse</div>
          </div>

          <div className="divide-y divide-tactical-border max-h-[600px] overflow-y-auto">
            {players.map(p => (
              <div key={p.id} data-testid={`admin-player-${p.id}`} className="p-4 hover:bg-tactical-elev transition-colors font-mono text-sm">
                {/* Mobile: stacked layout */}
                <div className="lg:hidden space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-white flex items-center gap-2 flex-wrap">
                        <span className="truncate">{p.display_name || (p.character ? `${p.character.first_name} ${p.character.last_name}` : p.username)}</span>
                        {p.banned && <span className="bg-tactical-danger/20 text-tactical-danger border border-tactical-danger/40 px-1.5 py-0.5 text-[9px] uppercase tracking-widest">BANNED</span>}
                        {p.role === "owner" && <span className="bg-volt/20 text-volt border border-volt/40 px-1.5 py-0.5 text-[9px] uppercase tracking-widest">OWNER</span>}
                        {p.role === "admin" && <span className="bg-tactical-warn/20 text-tactical-warn border border-tactical-warn/40 px-1.5 py-0.5 text-[9px] uppercase tracking-widest">ADMIN</span>}
                      </div>
                      <div className="text-[10px] text-white/40 truncate">{p.username} · {p.email || "—"}</div>
                      <div className="text-white/70 mt-1">
                        {p.character?.job_grade_label} · Lvl {p.character?.level}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-[10px] uppercase tracking-widest">
                    <div><span className="text-white/40">Wealth:</span> <span className="text-white/70">${fmt((p.character?.cash || 0) + (p.character?.bank || 0))}</span></div>
                    <div><span className="text-white/40">Vehicles:</span> <span className="text-white/70">{p.vehicle_count}</span></div>
                    <div><span className="text-white/40">Warns:</span> <span className="text-tactical-warn">{p.warning_count}</span></div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button data-testid={`view-${p.id}`} onClick={() => openDetail(p.id)} className="flex-1 border border-tactical-border hover:border-white px-3 py-1.5 text-[10px] uppercase tracking-widest transition-colors">Ansicht</button>
                    <button data-testid={`warn-${p.id}`} onClick={() => setAction({ type: "warn", user: p })} className="flex-1 border border-tactical-warn/40 text-tactical-warn hover:bg-tactical-warn/10 px-3 py-1.5 text-[10px] uppercase tracking-widest transition-colors">Warn</button>
                    {p.banned ? (
                      <button data-testid={`unban-${p.id}`} onClick={() => unban(p.id)} className="flex-1 border border-tactical-success/40 text-tactical-success hover:bg-tactical-success/10 px-3 py-1.5 text-[10px] uppercase tracking-widest transition-colors">Unban</button>
                    ) : (
                      <button data-testid={`ban-${p.id}`} onClick={() => setAction({ type: "ban", user: p })} className="flex-1 border border-tactical-danger/40 text-tactical-danger hover:bg-tactical-danger/10 px-3 py-1.5 text-[10px] uppercase tracking-widest transition-colors">Ban</button>
                    )}
                  </div>
                </div>

                {/* Desktop: grid layout */}
                <div className="hidden lg:grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-3">
                    <div className="text-white flex items-center gap-2">
                      {p.display_name || (p.character ? `${p.character.first_name} ${p.character.last_name}` : p.username)}
                      {p.banned && <span className="bg-tactical-danger/20 text-tactical-danger border border-tactical-danger/40 px-1.5 py-0.5 text-[9px] uppercase tracking-widest">BANNED</span>}
                      {p.role === "owner" && <span className="bg-volt/20 text-volt border border-volt/40 px-1.5 py-0.5 text-[9px] uppercase tracking-widest">OWNER</span>}
                      {p.role === "admin" && <span className="bg-tactical-warn/20 text-tactical-warn border border-tactical-warn/40 px-1.5 py-0.5 text-[9px] uppercase tracking-widest">ADMIN</span>}
                    </div>
                    <div className="text-[10px] text-white/40">{p.username}{p.email ? ` · ${p.email}` : ""}</div>
                  </div>
                  <div className="col-span-3 text-white/70">
                    {p.character?.job_grade_label || "—"}
                    <div className="text-[10px] text-white/40">Job: {p.character?.job || "—"} · Lvl {p.character?.level}</div>
                  </div>
                  <div className="col-span-1 text-right text-white/60">${fmt((p.character?.cash || 0) + (p.character?.bank || 0))}</div>
                  <div className="col-span-1 text-right text-white/60">{p.vehicle_count} veh</div>
                  <div className="col-span-1 text-right text-tactical-warn">{p.warning_count}w</div>
                  <div className="col-span-3 flex items-center gap-2 justify-end">
                    <button onClick={() => openDetail(p.id)} className="border border-tactical-border hover:border-white px-3 py-1 text-[10px] uppercase tracking-widest transition-colors">Ansicht</button>
                    <button onClick={() => setAction({ type: "warn", user: p })} className="border border-tactical-warn/40 text-tactical-warn hover:bg-tactical-warn/10 px-3 py-1 text-[10px] uppercase tracking-widest transition-colors">Warn</button>
                    {p.banned ? (
                      <button onClick={() => unban(p.id)} className="border border-tactical-success/40 text-tactical-success hover:bg-tactical-success/10 px-3 py-1 text-[10px] uppercase tracking-widest transition-colors">Unban</button>
                    ) : (
                      <button onClick={() => setAction({ type: "ban", user: p })} className="border border-tactical-danger/40 text-tactical-danger hover:bg-tactical-danger/10 px-3 py-1 text-[10px] uppercase tracking-widest transition-colors">Ban</button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {players.length === 0 && (
              <div className="p-12 text-center font-mono text-white/40 text-sm">Keine Spieler gefunden</div>
            )}
          </div>
        </div>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl bg-tactical-surface border-tactical-border text-white">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display font-black text-2xl">
                  {detail.user.username}
                  {detail.character && <span className="text-white/50 font-normal text-lg ml-2">— {detail.character.first_name} {detail.character.last_name}</span>}
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-4 font-mono text-sm">
                <MiniStat icon={User} label="Job" value={detail.character?.job_grade_label || "—"} />
                <MiniStat icon={ChartLineUp} label="Level" value={detail.character?.level ?? "—"} />
                <MiniStat icon={Car} label="Fahrzeuge" value={detail.vehicles.length} />
                <MiniStat icon={House} label="Immobilien" value={detail.properties.length} />
                <MiniStat icon={Backpack} label="Inventar Items" value={detail.inventory.length} />
                <MiniStat icon={ShieldWarning} label="Strafakte" value={detail.record.length} />
              </div>

              {detail.vehicles.length > 0 && (
                <div>
                  <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest mb-2">Fahrzeuge</div>
                  <div className="border border-tactical-border max-h-40 overflow-y-auto">
                    {detail.vehicles.map(v => (
                      <div key={v.id} className="grid grid-cols-3 gap-2 px-3 py-2 border-b border-tactical-border/50 last:border-0 font-mono text-xs">
                        <div>{v.label}</div>
                        <div className="text-yellow-400">{v.plate}</div>
                        <div className="text-white/60 text-right">{v.garage}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {detail.warnings.length > 0 && (
                <div>
                  <div className="font-mono text-[10px] text-tactical-warn uppercase tracking-widest mb-2">Warnungen ({detail.warnings.length})</div>
                  <div className="border border-tactical-warn/30 max-h-32 overflow-y-auto">
                    {detail.warnings.map(w => (
                      <div key={w.id} className="px-3 py-2 border-b border-tactical-border/50 last:border-0 font-mono text-xs">
                        <div className="text-white">{w.reason}</div>
                        <div className="text-[10px] text-white/40">{w.admin} · {new Date(w.date).toLocaleString("de-DE")}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Action dialog */}
      <Dialog open={!!action} onOpenChange={(o) => !o && setAction(null)}>
        <DialogContent className="bg-tactical-surface border-tactical-border text-white">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl">
              {action?.type === "warn" ? "Spieler warnen" : "Spieler bannen"}
              <span className="text-white/50 font-normal text-base ml-2">— {action?.user.username}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-mono text-white/50 uppercase tracking-wider mb-2">Grund</label>
              <textarea
                data-testid="action-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                className="w-full bg-black/60 border border-tactical-border focus:border-volt outline-none px-3 py-2 font-mono text-sm transition-colors duration-150"
                placeholder="Regelverstoß..."
              />
            </div>
            {action?.type === "ban" && (
              <div className="grid grid-cols-2 gap-4">
                <label className="flex items-center gap-2 font-mono text-sm cursor-pointer">
                  <input data-testid="perm-checkbox" type="checkbox" checked={permanent} onChange={(e) => setPermanent(e.target.checked)} />
                  Permanent
                </label>
                {!permanent && (
                  <div>
                    <label className="block text-xs font-mono text-white/50 uppercase tracking-wider mb-1">Tage</label>
                    <input data-testid="ban-days" type="number" value={days} onChange={(e) => setDays(e.target.value)} min={1} className="w-full bg-black/60 border border-tactical-border focus:border-volt outline-none px-3 py-2 font-mono text-sm" />
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <button onClick={() => setAction(null)} className="border border-tactical-border hover:border-white px-4 py-2 text-xs uppercase tracking-widest font-mono transition-colors">
              <X size={14} className="inline mr-1" /> Abbrechen
            </button>
            <button
              data-testid="submit-action"
              onClick={submitAction}
              className={`px-4 py-2 text-xs uppercase tracking-widest font-mono border transition-colors ${
                action?.type === "warn"
                  ? "border-tactical-warn bg-tactical-warn/20 text-tactical-warn hover:bg-tactical-warn/30"
                  : "border-tactical-danger bg-tactical-danger/20 text-tactical-danger hover:bg-tactical-danger/30"
              }`}
            >
              Bestätigen
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatBox({ icon: Icon, label, value, tone = "default", testId }) {
  const tc = { default: "text-white", volt: "text-volt", warn: "text-tactical-warn", danger: "text-tactical-danger" }[tone];
  return (
    <div data-testid={testId} className="border border-tactical-border bg-tactical-surface p-5">
      <div className="flex items-center justify-between mb-3">
        <Icon size={18} className={tc} />
        <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest">{label}</div>
      </div>
      <div className={`font-mono font-bold text-3xl ${tc}`}>{value}</div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, value }) {
  return (
    <div className="border border-tactical-border p-3 flex items-center gap-3">
      <Icon size={16} className="text-volt" />
      <div className="flex-1">
        <div className="text-[10px] text-white/40 uppercase tracking-widest">{label}</div>
        <div className="text-white">{value}</div>
      </div>
    </div>
  );
}

function SyncCell({ label, value, tone = "text-white" }) {
  return (
    <div className="border border-tactical-border/60 p-3">
      <div className="text-[9px] text-white/40 uppercase tracking-widest">{label}</div>
      <div className={`mt-1 truncate ${tone}`}>{value}</div>
    </div>
  );
}
